import { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { useSessionOrNull } from '~/contexts/SessionContext';
import { hasUnsavedChangesState } from '~/state/atoms/patientAtoms';

/**
 * Renderless single writer of the legacy {@link hasUnsavedChangesState}
 * boolean: derives it from the session's {@link PendingChangesStore}
 * (structure-set dirty state plus out-of-scope changes like registration
 * edits) instead of the scattered `setHasUnsavedChanges(true)` calls the
 * command hooks used to make.
 *
 * `useUnloadPatient` still writes the atom false during its synchronous
 * teardown — that is a reset of the same derivation (store disposed with
 * the session), not a competing source of truth.
 */
export function PendingChangesRecoilBridge() {
    const session = useSessionOrNull();
    const setHasUnsavedChanges = useSetRecoilState(hasUnsavedChangesState);

    useEffect(() => {
        if (!session) {
            setHasUnsavedChanges(false);
            return;
        }
        const store = session.pendingChangesStore;
        const sync = (): void => {
            setHasUnsavedChanges(store.getState().hasChanges);
        };
        sync();
        return store.subscribe(sync);
    }, [session, setHasUnsavedChanges]);

    return null;
}
